import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  submitResignation,
  getMyResignation,
  withdrawResignation,
} from "../../api/resignation";
import { uploadAttachments } from "../../api/attachments";
import {
  RESIGN_STATUS,
  sanitizeResignationForViewer,
  getActiveResignationForEmployee,
  canWithdrawResignation,
  getWithdrawDaysRemaining,
  getResignStatusLabel,
  mapApiResignStatus,
  getManagerChainEmployeeIds,
} from "../../utils/resignHelpers";
import AttachmentUploader from "../common/AttachmentUploader";
import AttachmentList from "../common/AttachmentList";
import "./ResignRequestForm.css";

function mapApiResignationToLocal(apiResignation, user) {
  if (!apiResignation) return null;
  return {
    id: apiResignation.id,
    employeeId: user?.id,
    employeeName: user?.name,
    employeeRole: user?.role,
    lastWorkingDay: apiResignation.lastWorkingDay,
    submittedAt: apiResignation.createdAt,
    status: mapApiResignStatus(apiResignation.status),
    canWithdraw: apiResignation.canWithdraw,
    daysUntilWithdrawalExpires: apiResignation.daysUntilWithdrawalExpires,
    withdrawalDeadline: apiResignation.withdrawalDeadline,
  };
}

function ResignRequestForm() {
  const { user, setIsResigned } = useAuth();
  const {
    resignations,
    addResignation,
    updateResignations,
    getEmployeeById,
    addRecentActivity,
  } = useData();

  const [lastWorkingDay, setLastWorkingDay] = useState("");
  const [reason, setReason] = useState("");
  const [handoverNotes, setHandoverNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiResignation, setApiResignation] = useState(null);

  const localActiveResignation = useMemo(
    () => getActiveResignationForEmployee(resignations, user?.id),
    [resignations, user?.id],
  );

  const activeResignation = useMemo(() => {
    if (
      apiResignation &&
      mapApiResignStatus(apiResignation.status) === RESIGN_STATUS.PENDING
    ) {
      return mapApiResignationToLocal(apiResignation, user);
    }
    return localActiveResignation;
  }, [apiResignation, localActiveResignation, user]);

  const withdrawnResignation = useMemo(
    () =>
      resignations.find(
        (r) =>
          r.employeeId === user?.id &&
          r.status === RESIGN_STATUS.WITHDRAWN &&
          r.showWithdrawnNotice,
      ),
    [resignations, user?.id],
  );

  const employeeView = useMemo(
    () =>
      sanitizeResignationForViewer(
        activeResignation || withdrawnResignation,
        user?.role,
      ),
    [activeResignation, withdrawnResignation, user?.role],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadMyResignation() {
      try {
        const data = await getMyResignation();
        if (!cancelled) {
          setApiResignation(data);
        }
      } catch {
        // No active resignation on server — keep local state as-is
      }
    }

    loadMyResignation();
    return () => {
      cancelled = true;
    };
  }, [user?.id, localActiveResignation?.id]);

  function getMinLastWorkingDay() {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 15);
    return minDate.toISOString().split("T")[0];
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!lastWorkingDay) {
      setError("من فضلك أدخل آخر يوم عمل");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(lastWorkingDay);
    selectedDate.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil(
      (selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays < 15) {
      setError("آخر يوم عمل لازم يكون بعد 15 يوم على الأقل من النهاردة");
      return;
    }

    if (!reason.trim()) {
      setError("من فضلك أدخل سبب الاستقالة");
      return;
    }

    if (activeResignation) {
      setError("لديك طلب استقالة قيد المراجعة بالفعل");
      return;
    }

    setSubmitting(true);
    try {
      const apiResult = await submitResignation({
        lastWorkingDay,
        reason: reason.trim(),
        handoverNotes: handoverNotes.trim() || undefined,
      });

      if (files.length && apiResult?.id) {
        try {
          await uploadAttachments("resignation", apiResult.id, files);
        } catch {
          // Resignation already submitted — surface soft failure via fileNames only
        }
      }

      setApiResignation(apiResult);

      const employeeRecord =
        (user?.employeeId && getEmployeeById(user.employeeId)) ||
        getEmployeeById(user.id);
      const departmentManagerId = employeeRecord?.departmentManagerId ?? null;
      const managerId = employeeRecord?.managerId ?? null;
      const managerChainIds = employeeRecord?.id
        ? getManagerChainEmployeeIds(employeeRecord.id, getEmployeeById)
        : managerId
          ? [managerId]
          : [];

      const now = new Date().toISOString();
      const resignationData = {
        id: apiResult?.id || Date.now(),
        employeeId: user.id,
        employeeRecordId: employeeRecord?.id || user.employeeId || null,
        employeeName: user.name,
        employeeRole: user.role,
        managerId,
        managerChainIds,
        departmentManagerId,
        lastWorkingDay,
        reason: reason.trim(),
        handoverNotes: handoverNotes.trim(),
        fileNames: files.map((f) => f.name),
        submittedAt: now,
        status: RESIGN_STATUS.PENDING,
      };

      addResignation(resignationData);

      addRecentActivity({
        id: Date.now(),
        text: `طلب استقالة من ${user.name}`,
        date: new Date().toLocaleDateString("ar-EG"),
        type: "resignation",
      });

      setLastWorkingDay("");
      setReason("");
      setHandoverNotes("");
      setFiles([]);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "حدث خطأ أثناء تقديم طلب الاستقالة",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleWithdraw() {
    if (!activeResignation) return;

    const canWithdraw =
      activeResignation.canWithdraw ??
      canWithdrawResignation(activeResignation);

    if (!canWithdraw) {
      setError("لا يمكن سحب الاستقالة بعد مرور 7 أيام من تاريخ التقديم");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await withdrawResignation();
      setApiResignation(null);
      setIsResigned(false);

      updateResignations((prev) =>
        prev.map((r) =>
          r.id === activeResignation.id ||
          (r.employeeId === user?.id && r.status === RESIGN_STATUS.PENDING)
            ? {
                ...r,
                status: RESIGN_STATUS.WITHDRAWN,
                withdrawnAt: new Date().toISOString(),
                showWithdrawnNotice: true,
              }
            : r,
        ),
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "حدث خطأ أثناء سحب الاستقالة",
      );
    } finally {
      setSubmitting(false);
    }
  }

  function dismissWithdrawnNotice() {
    if (!withdrawnResignation) return;
    updateResignations((prev) =>
      prev.map((r) =>
        r.id === withdrawnResignation.id
          ? { ...r, showWithdrawnNotice: false }
          : r,
      ),
    );
  }

  if (activeResignation && employeeView) {
    const canWithdraw =
      activeResignation.canWithdraw ??
      canWithdrawResignation(activeResignation);
    const daysLeft =
      activeResignation.daysUntilWithdrawalExpires ??
      getWithdrawDaysRemaining(activeResignation);

    return (
      <div className="resign-form-card">
        <div className="resign-success-box">
          <p className="resign-success-title">✅ تم تقديم طلب الاستقالة</p>
          <p className="resign-success-subtitle">
            تم إرسال طلب الاستقالة إلى مدرائك حسب التدرج الوظيفي وإلى الموارد
            البشرية والمدير العام. يمكنك استخدام حسابك بشكل طبيعي حتى آخر يوم
            عمل.
          </p>

          <div className="resign-info-box">
            <div className="resign-info-row">
              <span className="resign-info-label">تاريخ التقديم:</span>
              <span className="resign-info-value">
                {new Date(employeeView.submittedAt).toLocaleDateString("ar-EG")}
              </span>
            </div>
            <div className="resign-info-row">
              <span className="resign-info-label">آخر يوم عمل:</span>
              <span className="resign-info-value">
                {new Date(employeeView.lastWorkingDay).toLocaleDateString(
                  "ar-EG",
                )}
              </span>
            </div>
            <div className="resign-info-row">
              <span className="resign-info-label">الحالة:</span>
              <span className="resign-status-badge resign-status-pending">
                {getResignStatusLabel(employeeView.status)}
              </span>
            </div>
          </div>

          <p className="resign-privacy-note">
            🔒 سبب الاستقالة سري ولا يمكنك الاطلاع عليه بعد التقديم. يطلع عليه
            مدير الإدارة والموارد البشرية والمدير العام.
          </p>

          {activeResignation.id && (
            <AttachmentList
              entityType="resignation"
              entityId={activeResignation.id}
              showDelete={canWithdraw}
              compact
            />
          )}

          {canWithdraw ? (
            <div className="resign-withdraw-section">
              <p className="resign-withdraw-note">
                ⚠️ يمكنك سحب الاستقالة خلال {daysLeft} أيام بدون إبداء أسباب
              </p>
              <button
                className="resign-withdraw-btn"
                onClick={handleWithdraw}
                disabled={submitting}
              >
                {submitting ? "جارٍ السحب..." : "🔄 سحب الاستقالة"}
              </button>
            </div>
          ) : (
            <p className="resign-withdraw-expired">
              ⏰ انتهت فترة السحب (7 أيام)
            </p>
          )}

          {error && <p className="resign-error">{error}</p>}
        </div>
      </div>
    );
  }

  if (withdrawnResignation && employeeView) {
    return (
      <div className="resign-form-card">
        <div className="resign-success-box">
          <p className="resign-success-title">🔄 تم سحب الاستقالة</p>
          <p className="resign-success-subtitle">
            تم سحب طلب الاستقالة بنجاح في تاريخ{" "}
            {new Date(withdrawnResignation.withdrawnAt).toLocaleDateString(
              "ar-EG",
            )}
          </p>
          <button
            className="resign-secondary-btn"
            onClick={dismissWithdrawnNotice}
          >
            تقديم طلب جديد
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="resign-form-card" onSubmit={handleSubmit} dir="rtl">
      <div className="resign-field">
        <label className="resign-label">آخر يوم عمل متوقع *</label>
        <input
          type="date"
          className="resign-input"
          value={lastWorkingDay}
          min={getMinLastWorkingDay()}
          onChange={(e) => setLastWorkingDay(e.target.value)}
        />
        <div className="resign-note">
          ⚠️ يجب أن يكون آخر يوم عمل بعد 15 يوم على الأقل من تاريخ اليوم.
        </div>
      </div>

      <div className="resign-field">
        <label className="resign-label">سبب الاستقالة *</label>
        <textarea
          className="resign-textarea"
          rows={4}
          placeholder="اكتب سبب الاستقالة باختصار..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="resign-note">
          🔒 لن تتمكن من الاطلاع على السبب بعد التقديم. يطلع عليه مدير الإدارة
          والموارد البشرية والمدير العام.
        </div>
      </div>

      <div className="resign-field">
        <label className="resign-label">ملاحظات تسليم العمل (اختياري)</label>
        <textarea
          className="resign-textarea"
          rows={3}
          placeholder="اكتب أي ملاحظات عن المهام أو الملفات المحتاجة تسليمها..."
          value={handoverNotes}
          onChange={(e) => setHandoverNotes(e.target.value)}
        />
      </div>

      <div className="resign-field">
        <AttachmentUploader
          entityType="resignation"
          files={files}
          onFilesChange={setFiles}
          disabled={submitting}
          label="مستندات داعمة (اختياري)"
        />
      </div>

      {error && <p className="resign-error">{error}</p>}

      <div className="resign-footer-note">
        ⚠️ تنبيه: طلب الاستقالة يُرسل لمدرائك حسب التدرج الوظيفي وللموارد البشرية
        والمدير العام، ويمكنك سحبه خلال 7 أيام من تاريخ التقديم بدون إبداء أسباب.
        حسابك يبقى مفتوحاً بشكل طبيعي حتى آخر يوم عمل.
      </div>

      <button type="submit" className="resign-submit-btn" disabled={submitting}>
        {submitting ? "جارٍ التقديم..." : "تقديم طلب الاستقالة"}
      </button>
    </form>
  );
}

export default ResignRequestForm;
